import React, { FC } from "react";
import { Banner } from "@ldp-web/component-library/ui/molecules/banner";
import { WrapperSection } from "@ldp-web/component-library/ui/atoms/section";
import {
  IHeaderGroupComponentProps,
  TImageType,
  TSDMCta,
} from "@ldp/common-types";
import { useRedirectLink } from "../../utils";
import { useSDMRouterContext } from "@ldp/sdm-contexts";
import { HeaderGroupComponent } from "../header-group/header-group.component";
import { useAnalyticsContext, useFeatureFlags } from "@ldp/common-contexts";
import {
  SDMAnalyticEvents,
  TAnalyticsBannerWithHeadlineContext,
  TSDMBannerContextAnalytics,
  TSDMComponentAnalyticsObject,
  getCommonComponentContext,
} from "@ldp/sdm-analytics-utils";
import { StampPlacementType, TStamp } from "@ldp-web/component-library/types";

/** Banner with headline component props */
export interface IBannerWithHeadlineComponentProps {
  headline: string;
  /** headline description  */
  displayText1: string;
  /** banner title */
  displayText2: string;
  /** banner description */
  displayText3: string;
  /** hero image alt text */
  desktopImage: TImageType;
  /** tablet image */
  tabletImage: TImageType;
  /** mobile image */
  mobileImage: TImageType;
  /** background color */
  backgroundColor?: string;
  /** first logo */
  logo1?: TImageType;
  /** first logo */
  logo2?: TImageType;
  /** call to action */
  callToAction: TSDMCta;
  /** header group details */
  headerGroup?: IHeaderGroupComponentProps;
  /** content id */
  contentId?: string;
  /** component id */
  componentId?: string;
  /** component analytics */
  analytics?: TSDMComponentAnalyticsObject;
  /** stamp props - content, textColour, backgroundColour */
  stamp?: TStamp;
  /** stamp position that accepts bottom-right, bottom-left, top-left, top-right */
  stampPlacement?: StampPlacementType;
  /** id of component */
  id?: string;
}

/** Renders Banner component with Headline */
export const BannerWithHeadlineComponent: FC<
  IBannerWithHeadlineComponentProps
> = (props) => {
  const {
    headline,
    displayText1,
    displayText2,
    displayText3,
    desktopImage,
    tabletImage,
    mobileImage,
    backgroundColor,
    logo1,
    logo2,
    callToAction,
    headerGroup,
    componentId,
    contentId,
    analytics,
    stamp,
    stampPlacement,
    id,
  } = props;

  const router = useSDMRouterContext();
  const redirectToPage = useRedirectLink(router);
  const { trackEvent } = useAnalyticsContext();
  const { getFeatureFlags } = useFeatureFlags();
  const { EnableHeliosSnowplow: snowplowEnabled } = getFeatureFlags();

  const trackFnWrapper = (type: string, context: unknown): void => {
    const { name: actionName, actionType } =
      context as TSDMBannerContextAnalytics;
    const dsModelData = analytics?.dsModel;
    const genericAnalytics = analytics?.genericAnalytics;
    const engineValue = dsModelData?.engine || "";
    const commonContext = getCommonComponentContext(
      snowplowEnabled,
      true,
      dsModelData,
      genericAnalytics
    );
    const eventTrackingContext: TAnalyticsBannerWithHeadlineContext = {
      event: type as SDMAnalyticEvents,
      component: {
        id: contentId || "",
        type: componentId || "",
        name: actionName || "",
        engine: engineValue,
        click_element: callToAction,
        click_action: callToAction?.text,
        click_url: callToAction?.url,
      },
      customEngagement: {
        action: actionType,
      },
      ...commonContext,
    };

    trackEvent("heliosBannerWithHeadline", eventTrackingContext);
  };

  const headerGroupProps: IHeaderGroupComponentProps = headerGroup ?? {
    headline: {
      text: headline,
      variantId: "MEDIUM_03",
      callToAction: callToAction,
      clickHandler: redirectToPage,
    },
    logoUrl: logo1?.url,
    logoAltText: logo1?.altText,
    logoUrl2: logo2?.url,
    logo2AltText: logo2?.altText,
    paragraph: { text: displayText1, variantId: "REGULAR_01" },
  };

  return (
    <>
      <WrapperSection mb={0} id={id}>
        <HeaderGroupComponent {...headerGroupProps} noWrapper />
      </WrapperSection>
      <Banner
        innerContentBackgroundColor={backgroundColor}
        variant="headline"
        title={displayText2}
        bodyText={displayText3}
        desktopImage={desktopImage}
        tabletImage={tabletImage}
        mobileImage={mobileImage}
        backgroundColor="transparent"
        promoCTA1={callToAction}
        marginBottom="3rem"
        buttonClickHandler={redirectToPage}
        trackingFn={trackFnWrapper}
        stamp={stamp}
        stampPlacement={stampPlacement}
      />
    </>
  );
};

export default BannerWithHeadlineComponent;
